/**
 * Shop Coffee Tabs — section-shop-coffee.js
 *
 * Custom element <shop-coffee-tabs> that manages:
 * - Tab switching with full ARIA state (role="tab", aria-selected, aria-hidden)
 * - Lazy Swiper initialization:
 *     · First tab → initialized immediately on connectedCallback (element is visible)
 *     · Other tabs → initialized on first click (element is visible by then)
 *     · Already-initialized tabs → swiper.update() to refresh dimensions
 * - Each panel owns its own navigation elements → zero conflicts between
 *   multiple instances of this section on the same page
 * - Shopify Theme Editor compatibility: shopify:section:load triggers full re-init
 */

class ShopCoffeeTabs extends HTMLElement {
  connectedCallback() {
    /** @type {Map<string, Swiper>} blockId → Swiper instance */
    this._swipers = new Map();

    /** @type {string|null} */
    this._activeBlockId = null;

    this._sectionId = this.dataset.sectionId;
    this._desktopMq = window.matchMedia('(min-width: 768px)');
    this._isSliderEnabled = this._desktopMq.matches;

    this._setup();

    this._onViewportChange = () => this._handleViewportChange();
    if (this._desktopMq.addEventListener) {
      this._desktopMq.addEventListener('change', this._onViewportChange);
    } else {
      this._desktopMq.addListener(this._onViewportChange);
    }

    // Shopify editor: section settings changed → element is re-rendered
    this._onSectionLoad = (e) => {
      if (e.detail.sectionId !== this._sectionId) return;
      this._teardown();
      this._setup();
    };
    document.addEventListener('shopify:section:load', this._onSectionLoad);
  }

  disconnectedCallback() {
    this._teardown();
    document.removeEventListener('shopify:section:load', this._onSectionLoad);
    if (this._desktopMq) {
      if (this._desktopMq.removeEventListener) {
        this._desktopMq.removeEventListener('change', this._onViewportChange);
      } else {
        this._desktopMq.removeListener(this._onViewportChange);
      }
    }
  }

  // ─── SETUP / TEARDOWN ────────────────────────────────────────────────────

  _setup() {
    this._isSliderEnabled = this._desktopMq.matches;
    this._swipers.clear();
    this._activeBlockId = null;

    this._bindTabButtons();

    const firstPanel = this.querySelector('[data-tab-panel].is-active');
    if (firstPanel) {
      this._activeBlockId = firstPanel.dataset.blockId;
      this._initSwiperForPanel(firstPanel);
    }
  }

  _teardown() {
    this._swipers.forEach((swiper) => {
      if (!swiper.destroyed) swiper.destroy(true, true);
    });
    this._swipers.clear();
  }

  // ─── TAB BINDING ─────────────────────────────────────────────────────────

  _bindTabButtons() {
    this.querySelectorAll('[data-tab-btn]').forEach((btn) => {
      // Remove any stale handler from a previous _setup() call
      if (btn._scClickHandler) {
        btn.removeEventListener('click', btn._scClickHandler);
      }
      btn._scClickHandler = () => this._onTabClick(btn);
      btn.addEventListener('click', btn._scClickHandler);
    });
  }

  _onTabClick(btn) {
    const blockId = btn.dataset.blockId;
    if (blockId === this._activeBlockId) return;
    this._activateTab(btn);
  }

  // ─── TAB ACTIVATION ──────────────────────────────────────────────────────

  _activateTab(btn) {
    const blockId    = btn.dataset.blockId;
    const shopAllUrl = btn.dataset.shopAllUrl;

    // ── Tab button ARIA state ──
    this.querySelectorAll('[data-tab-btn]').forEach((b) => {
      const active = b === btn;
      b.classList.toggle('is-active', active);
      b.setAttribute('aria-selected', active ? 'true' : 'false');
    });

    // ── Update Shop All href ──
    const shopAllLink = this.querySelector('[data-shop-all-link]');
    if (shopAllLink) {
      if (shopAllUrl) {
        shopAllLink.setAttribute('href', shopAllUrl);
        shopAllLink.removeAttribute('hidden');
      } else {
        shopAllLink.setAttribute('hidden', '');
      }
    }

    // ── Switch panels ──
    let targetPanel = null;
    this.querySelectorAll('[data-tab-panel]').forEach((panel) => {
      const active = panel.dataset.blockId === blockId;
      panel.classList.toggle('is-active', active);
      panel.setAttribute('aria-hidden', active ? 'false' : 'true');
      if (active) targetPanel = panel;
    });

    if (!targetPanel) return;
    this._activeBlockId = blockId;

    if (!this._isSliderEnabled) return;

    // ── Swiper: initialize once, update on revisit ──
    if (this._swipers.has(blockId)) {
      // Panel was shown before — recalculate layout in case container resized
      this._swipers.get(blockId).update();
    } else {
      // First time this panel becomes visible — safe to initialize Swiper now
      this._initSwiperForPanel(targetPanel);
    }
  }

  // ─── SWIPER ──────────────────────────────────────────────────────────────

  _initSwiperForPanel(panel) {
    if (!this._isSliderEnabled) return;
    const swiperEl = panel.querySelector('.swiper');
    if (!swiperEl) return;

    if (typeof Swiper === 'undefined') {
      console.warn('[shop-coffee-tabs] Swiper is not available.');
      return;
    }

    const config = this._buildSwiperConfig(panel);
    const swiper = new Swiper(swiperEl, config);
    this._swipers.set(panel.dataset.blockId, swiper);
  }

  _handleViewportChange() {
    const shouldEnableSlider = this._desktopMq.matches;
    if (shouldEnableSlider === this._isSliderEnabled) return;

    this._isSliderEnabled = shouldEnableSlider;

    if (!shouldEnableSlider) {
      this._teardown();
      this._bindTabButtons();
      return;
    }

    const activePanel = this.querySelector('[data-tab-panel].is-active');
    if (activePanel) {
      this._initSwiperForPanel(activePanel);
    }
  }

  /**
   * Build Swiper config scoped to a specific panel.
   * Navigation/pagination elements are queried inside `panel`, not globally,
   * so multiple sections on the same page never share DOM references.
   *
   * @param {HTMLElement} panel
   * @returns {Object}
   */
  _buildSwiperConfig(panel) {
    const gap           = parseInt(this.dataset.spaceBetween || '16', 10);
    const desktopGap    = 40;
    const slidesDesktop = parseFloat(this.dataset.slidesDesktop || '5');
    const slidesTablet  = parseFloat(this.dataset.slidesTablet  || '3');
    const slidesMobile  = parseFloat(this.dataset.slidesMobile  || '2');
    const showArrows    = this.dataset.showArrows    === 'true';
    const showDots      = this.dataset.showDots      === 'true';
    const showScrollbar = this.dataset.showScrollbar === 'true';

    const config = {
      slidesPerView: slidesMobile,
      spaceBetween:  Math.round(gap * 0.6),
      grabCursor:    true,
      watchSlidesProgress: true,
      centerInsufficientSlides: true,
      breakpoints: {
        768: {
          slidesPerView: slidesTablet,
          spaceBetween:  Math.round(gap * 0.8),
        },
        992: {
          slidesPerView: slidesDesktop,
          spaceBetween:  desktopGap,
        },
      },
    };

    if (showArrows) {
      config.navigation = {
        prevEl: panel.querySelector('[data-swiper-prev]'),
        nextEl: panel.querySelector('[data-swiper-next]'),
      };
    }

    if (showDots) {
      config.pagination = {
        el:        panel.querySelector('[data-swiper-pagination]'),
        clickable: true,
      };
    }

    if (showScrollbar) {
      config.scrollbar = {
        el:        panel.querySelector('[data-swiper-scrollbar]'),
        draggable: true,
      };
    }

    return config;
  }
}

if (!customElements.get('shop-coffee-tabs')) {
  customElements.define('shop-coffee-tabs', ShopCoffeeTabs);
}
